/**
 * CartPage Component
 * Displays the shopping cart and allows users to manage cart items
 * 
 * @module components/pages/CartPage
 * @requires react
 * @requires context/CartContext
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import './CartPage.css';

/**
 * CartPage component
 * @returns {JSX.Element} Cart page
 */
const CartPage = () => {
  // Get cart data and functions from cart context
  const { 
    cart, 
    loading, 
    error, 
    removeFromCart, 
    updateQuantity, 
    getTotal 
  } = useCart();
  
  // Use navigate for programmatic navigation
  const navigate = useNavigate();
  // Add state for checkout processing
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);

  /**
   * Handle quantity change for an item
   * @param {string} productId - ID of the product
   * @param {Event} e - Change event
   */
  const handleQuantityChange = (productId, e) => {
    const value = parseInt(e.target.value);
    if (value > 0) {
      updateQuantity(productId, value);
    }
  };

  /**
   * Handle removing an item from the cart
   * @param {string} productId - ID of the product to remove
   */
  const handleRemoveItem = (productId) => {
    removeFromCart(productId);
  };
  
  /**
   * Handle proceeding to checkout
   * Prepares cart data and navigates to checkout
   */
  const handleCheckout = () => {
    // Prevent multiple clicks
    if (isProcessingCheckout) return;
    
    setIsProcessingCheckout(true);
    
    try {
      // Check if we have a cart with an ID
      if (cart?.items?.length > 0) {
        navigate('/checkout/');
      } else {
        console.error('No cart ID available for checkout');
        alert('There was an issue processing your cart. Please try again.');
        setIsProcessingCheckout(false);
      }
    } catch (error) {
      console.error('Error proceeding to checkout:', error);
      setIsProcessingCheckout(false);
    }
  };

  // Show loading message
  if (loading) {
    return <div className="cart-page loading">Loading cart...</div>;
  }

  // Show error message
  if (error) {
    return <div className="cart-page error">{error}</div>;
  }

  // Show empty cart message
  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="cart-page empty">
        <h2>Your Cart is Empty</h2>
        <p>Looks like you haven't added any soil mixes to your cart yet.</p>
        <Link to="/products" className="continue-shopping-btn">
          Browse Soil Mixes
        </Link>
      </div>
    );
  }

  // Calculate the total price
  const totalPrice = getTotal();

  return (
    <div className="cart-page">
      <h2>Your Shopping Cart</h2>
      
      <div className="cart-items">
        {cart.items.map((item) => (
          <div key={item.product} className="cart-item">
            <div className="item-details">
              <h3>{item.name}</h3>
              <p className="item-price">${item.price.toFixed(2)}</p>
            </div>
            
            <div className="item-actions">
              <div className="quantity-control">
                <label htmlFor={`quantity-${item.product}`}>Qty:</label>
                <input
                  type="number"
                  id={`quantity-${item.product}`}
                  min="1"
                  value={item.quantity}
                  onChange={(e) => handleQuantityChange(item.product, e)}
                />
              </div>
              
              <div className="item-subtotal">
                ${(item.price * item.quantity).toFixed(2)}
              </div>
              
              <button
                className="remove-item-btn"
                onClick={() => handleRemoveItem(item.product)}
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </div>
        ))}
      </div>
      
      <div className="cart-summary">
        <div className="summary-row">
          <span>Subtotal:</span>
          <span>${totalPrice.toFixed(2)}</span>
        </div>
        <div className="summary-row">
          <span>Shipping:</span>
          <span>Calculated at checkout</span>
        </div>
        <div className="summary-row total">
          <span>Total:</span>
          <span>${totalPrice.toFixed(2)}</span>
        </div>
        
        <div className="cart-actions">
          <Link to="/products" className="continue-shopping-btn">
            Continue Shopping
          </Link>
          {/* Added loading state and better disabled condition */}
          <button 
            className="checkout-btn" 
            onClick={handleCheckout} 
            disabled={isProcessingCheckout || !cart.items || cart.items.length === 0}
          >
            {isProcessingCheckout ? "Processing..." : "Proceed to Checkout"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartPage;